import { useState } from "react";
import { trpc } from "@/lib/trpc";
import { Dialog } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AlertCircle, Loader2, Layers } from "lucide-react";
import { toast } from "sonner";
import type { PageItem } from "./types";

interface DeletePageDialogProps {
  page: PageItem | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onOpenManageSections?: (page: PageItem) => void;
  onSuccess?: () => void;
}

export function DeletePageDialog({
  page,
  open,
  onOpenChange,
  onOpenManageSections,
  onSuccess,
}: DeletePageDialogProps) {
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const utils = trpc.useUtils();

  const deleteMutation = trpc.pages.deletePage.useMutation({
    onSuccess: () => {
      toast.success(`Page "${page?.title}" deleted successfully.`);
      utils.pages.listPages.invalidate();
      utils.profile.getMyProfile.invalidate();
      onOpenChange(false);
      onSuccess?.();
    },
    onError: (err: { message?: string }) => {
      setErrorMsg(err.message || "Failed to delete page.");
    },
  });

  if (!page) return null;

  const hasSections = (page.sectionCount || 0) > 0;

  const handleDelete = () => {
    setErrorMsg(null);
    deleteMutation.mutate({ pageId: page.id });
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={`Delete Page: ${page.title}`}
      description="This action permanently deletes the page route from your academic portfolio."
    >
      <div className="space-y-4 pt-2">
        {errorMsg && (
          <div className="flex items-start gap-2.5 p-3 rounded-lg bg-destructive/10 border border-destructive/20 text-xs text-destructive">
            <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
            <span>{errorMsg}</span>
          </div>
        )}

        {hasSections ? (
          <div className="p-4 rounded-xl bg-amber-50 dark:bg-amber-950/20 border border-amber-200/80 dark:border-amber-900/50 space-y-3">
            <div className="flex items-start gap-2.5 text-xs text-amber-800 dark:text-amber-300">
              <AlertCircle className="h-4 w-4 shrink-0 mt-0.5 text-amber-600 dark:text-amber-400" />
              <div>
                <p className="font-semibold">Page contains {page.sectionCount} active section(s)</p>
                <p className="mt-1 text-amber-700/90 dark:text-amber-400/90 leading-relaxed">
                  To prevent unintentional loss of profile content, pages containing sections cannot be deleted directly. Please move the sections to another page or delete them first.
                </p>
              </div>
            </div>

            {onOpenManageSections && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="w-full gap-2 border-amber-300 text-amber-900 hover:bg-amber-100 dark:border-amber-800 dark:text-amber-200"
                onClick={() => {
                  onOpenChange(false);
                  onOpenManageSections(page);
                }}
              >
                <Layers className="h-3.5 w-3.5" />
                Manage & Reassign Page Sections
              </Button>
            )}
          </div>
        ) : (
          <div className="text-xs text-muted-foreground space-y-2">
            <p>
              Are you sure you want to delete <strong className="text-foreground">/{page.slug}</strong>?
            </p>
            <p className="text-[11px] text-muted-foreground/80">
              Any direct visitors or search engines requesting this URL in the future will receive a 404 page with a link back to your profile Home.
            </p>
          </div>
        )}

        <div className="flex items-center justify-end gap-2 pt-3 border-t">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onOpenChange(false)}
            disabled={deleteMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            size="sm"
            disabled={hasSections || deleteMutation.isPending}
            onClick={handleDelete}
            className="gap-1.5"
          >
            {deleteMutation.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Delete Page
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
